import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { NewsList, PromotionsList, Salon, Service, ServicePage, SiteSettings } from 'api/api.types'
import { indexArray } from 'utils/indexArray'
import { getPageOptions } from 'utils/serviceOptions'
import type { RootState } from './index'

type DataState = {
  settings: SiteSettings | null,
  salons: Salon[],
  services: Service[],
  news: NewsList | null,
  promotions: PromotionsList | null,
}

/** A service available in the selected city, with its distinct prices there */
export type CityService = Service & {
  cityPrices: number[],
}

/** A service page with its options available in the selected city, in the page's order */
export type ServiceGroup = {
  page: ServicePage,
  options: Array<{ title: string, service: CityService }>,
}

const initialState: DataState = {
  settings: null,
  salons: [],
  services: [],
  news: null,
  promotions: null,
}

const slice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    setSettings: (state, action: PayloadAction<SiteSettings>) => {
      state.settings = action.payload
    },
    setSalons: (state, action: PayloadAction<Salon[]>) => {
      state.salons = action.payload
    },
    setServices: (state, action: PayloadAction<Service[]>) => {
      state.services = action.payload
    },
    setNews: (state, action: PayloadAction<NewsList | null>) => {
      state.news = action.payload
    },
    setPromotions: (state, action: PayloadAction<PromotionsList | null>) => {
      state.promotions = action.payload
    },
  },
})

export const {
  setSettings,
  setSalons,
  setServices,
  setNews,
  setPromotions,
} = slice.actions

export const dataReducer = slice.reducer

export const getSettings = (state: RootState) => state.data.settings
export const getSalons = (state: RootState) => state.data.salons
export const getServices = (state: RootState) => state.data.services
export const getCachedNews = (state: RootState) => state.data.news
export const getCachedPromotions = (state: RootState) => state.data.promotions

// Imported lazily to avoid a circular import between the two slices
const getSelectedCityId = (state: RootState) => state.appState.selectedCityId

export const getCities = createSelector(
  [getSettings],
  (settings) => settings?.cities ?? [],
)

export const getCitiesIndex = createSelector(
  [getCities],
  (cities) => (cities.length ? indexArray(cities, (item) => item.city.id) : null),
)

export const getSelectedCity = createSelector(
  [getSelectedCityId, getCitiesIndex],
  (selectedCityId, citiesIndex) => {
    if (!citiesIndex || selectedCityId === null) {
      return null
    }
    return citiesIndex[selectedCityId] ?? null
  },
)

export const getActiveSalons = createSelector(
  [getSelectedCityId, getSalons],
  (cityId, salons) => salons.filter((salon) => salon.cityId === cityId),
)

export const getSalonsIndex = createSelector(
  [getSalons],
  (salons) => (salons.length ? indexArray(salons, 'id') : null),
)

export const getServicesIndex = createSelector(
  [getServices],
  (services) => (services.length ? indexArray(services, 'id') : null),
)

export const getActiveServices = createSelector(
  [getSelectedCityId, getSalonsIndex, getServices],
  (cityId, salonIndex, services): CityService[] => {
    if (!salonIndex || !cityId) {
      return []
    }
    const citySalonIds = Object.values(salonIndex)
      .filter((salon) => salon.cityId === cityId)
      .map((salon) => salon.id)
    return services
      .filter((service) => service.salons.some((salonId) => salonIndex[salonId]?.cityId === cityId))
      .map((service) => ({
        ...service,
        cityPrices: Array.from(new Set(Object.entries(service.pricesBySalon)
          .filter(([salonId]) => citySalonIds.includes(Number(salonId)))
          .map(([, price]) => price))),
      }))
  },
)

export const getActiveServicesIndex = createSelector(
  [getActiveServices],
  (services) => (services.length ? indexArray(services, 'id') : null),
)

/** Active services grouped by their page; a page with one option is a group of one */
export const getActiveServiceGroups = createSelector(
  [getActiveServices],
  (services): ServiceGroup[] => {
    const servicesById = new Map(services.map((service) => [service.id, service]))
    const groups: ServiceGroup[] = []
    const seenPageIds = new Set<number>()
    services.forEach((service) => {
      const page = service.meta
      if (!page || seenPageIds.has(page.id)) {
        return
      }
      seenPageIds.add(page.id)
      const options = getPageOptions(page).reduce<ServiceGroup['options']>((acc, option) => {
        const optionService = servicesById.get(option.service.id)
        if (optionService) {
          acc.push({ title: option.title, service: optionService })
        }
        return acc
      }, [])
      if (!options.length) {
        options.push({ title: '', service })
      }
      groups.push({ page, options })
    })
    return groups
  },
)

export const getTopMenu = createSelector(
  [getSettings],
  (settings) => settings?.menu?.top ?? [],
)

export const getBottomMenu = createSelector(
  [getSettings],
  (settings) => settings?.menu?.bottom ?? [],
)
